import { useEffect, useState } from 'react';
import { useGroupChannel } from '@sendbird/uikit-react/GroupChannel/context';
import GroupChannelHeader from '@sendbird/uikit-react/GroupChannel/components/GroupChannelHeader';
import _ from 'lodash';
import { DateTime } from 'luxon';
import { useUserInfoQuery } from '@apis/query/user';
import { UserType } from '@asset/enum/UserEnum';
import { useChatPopupStore } from '@store/chat';
import UserAvatarProfile from '@components/common/UserAvatarProfile';
import { getStudentStudyStatus, getStudentStudyStatusText } from '@asset/function/commonFunctions';
import * as S from '@styles/chat/chat.style';
import { IconArrowLeft, IconDotsVertical, IconSpeakerphone, IconX } from '@tabler/icons-react';
import BasicArrowTooltip from '@components/common/tool-tip/BasicArrowTooltip';
import { useRouter } from 'next/router';
import { getManageStudentStudyContentUrlObject } from 'routes/studyContent';
import { StudyContentType } from '@asset/enum/materialEnum';
import { addTypeChannelMetaData } from '@asset/function/chat';
import DropdownActionMenu from '@components/common/DropdownActionMenu';
import { useTranslation } from 'next-i18next';
import { ProblemStatusModalType } from '@asset/enum/studyContentEnum';
import useSendbirdStateContext from '@sendbird/uikit-react/useSendbirdStateContext';
import { StudyStatusLight } from '@components/study-status/StudyStatusIndicator';
import { useStudentsExtrasQuery } from '@apis/query/student';
import { Button } from '../ui/button';
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
const CustomChannelHeader = ({
  GroupChannelHeaderProps,
  studentInfo,
  onCloseGroupChannel
}) => {
  const router = useRouter();
  const {
    state: {
      currentChannel
    }
  } = useGroupChannel();
  const globalState = useSendbirdStateContext();
  const sdk = globalState?.stores?.sdkStore?.sdk;
  const connectionState = sdk?.connectionState;
  const {
    data: user
  } = useUserInfoQuery();
  const isTeacher = user?.type === UserType.TEACHER;
  const {
    setIsChatPopupOpen
  } = useChatPopupStore();
  const {
    t
  } = useTranslation('common');
  const [channelMetaData, setChannelMetaData] = useState(currentChannel ? addTypeChannelMetaData(currentChannel.cachedMetaData) : null);
  useEffect(() => {
    const fetchMetaData = async () => {
      if (connectionState !== 'OPEN' || !currentChannel) {
        return;
      }
      try {
        const response = await currentChannel.getAllMetaData();
        setChannelMetaData(addTypeChannelMetaData(response));
      } catch (error) {
        console.error('Failed to fetch channel metadata:', error);
      }
    };
    fetchMetaData();
  }, [currentChannel, connectionState]);
  const {
    data: studentExtrasRaw
  } = useStudentsExtrasQuery(channelMetaData?.studentId ? [channelMetaData.studentId] : []);
  const studentExtras = studentExtrasRaw?.data[0];
  const isAnomalous = studentExtras?.isAnomalous;
  const isActive = studentExtras?.isActive;
  const solvedAt = studentExtras?.recentStudyStatusInfo?.studentCompletedAt ? DateTime.fromISO(studentExtras?.recentStudyStatusInfo?.studentCompletedAt) : null;
  const studyStatus = getStudentStudyStatus(isAnomalous, isActive);
  const {
    timeText,
    statusText
  } = getStudentStudyStatusText({
    solvedAt,
    elapsedTime: studentExtras?.recentStudyStatusInfo?.elapsedTime,
    studyItemType: studentExtras?.recentStudyStatusInfo?.studyItemType
  });
  const studyStatusData = studentExtras?.recentStudyStatusInfo;
  return /*#__PURE__*/_jsxs(S.GroupChannelHeader, {
    children: [/*#__PURE__*/_jsx(GroupChannelHeader, {
      ...GroupChannelHeaderProps,
      className: "drag-handle",
      renderLeft: () => isTeacher ? /*#__PURE__*/_jsx(Button, {
        variant: "Ghost",
        size: "Medium",
        icon: /*#__PURE__*/_jsx(IconArrowLeft, {}),
        onClick: () => onCloseGroupChannel(),
        className: "chat-header-button"
      }) : /*#__PURE__*/_jsx(_Fragment, {}),
      renderMiddle: () => /*#__PURE__*/_jsxs("div", {
        className: "flex items-center gap-2 cursor-default",
        children: [isTeacher && /*#__PURE__*/_jsxs("div", {
          className: "relative flex w-8 h-8 rounded-[50%]",
          children: [studentExtras && /*#__PURE__*/_jsx(UserAvatarProfile, {
            name: studentExtras.avatarName,
            variant: "beam"
          }), /*#__PURE__*/_jsx("div", {
            className: "absolute bottom-[-2px] right-[-2px]",
            children: /*#__PURE__*/_jsx("div", {
              className: `border-[2px] border-solid rounded-full border-my-gray-100 box-content`,
              children: /*#__PURE__*/_jsx(StudyStatusLight, {
                studyStatus: studyStatus
              })
            })
          })]
        }), /*#__PURE__*/_jsx("div", {
          style: {
            width: isTeacher ? 'calc(100% - 2rem)' : '100%'
          },
          children: /*#__PURE__*/_jsxs("div", {
            className: "text-body-lg-700 text-my-gray-10",
            children: [studentInfo && user && (isTeacher ? /*#__PURE__*/_jsxs("div", {
              className: "flex items-center gap-1",
              children: [studentInfo.name, studentInfo.school && /*#__PURE__*/_jsx("p", {
                className: "overflow-hidden text-label-sm-600 text-my-gray-50 text-ellipsis whitespace-nowrap",
                children: studentInfo.school.name
              })]
            }) : studentInfo.teacherName + ' 선생님'), isTeacher ? statusText ? /*#__PURE__*/_jsx(BasicArrowTooltip, {
              arrow: true,
              placement: "top",
              title: studyStatusData ? `${studyStatusData.course} / ${studyStatusData.materialType} / ${studyStatusData.materialName} | ${studyStatusData.indexNameType} ${studyStatusData.indexNumber} / ${studyStatusData.division} ${studyStatusData.subIndexNumber} | ${t(`common:studyStatus.${studyStatusData.studyStatusType}`)}` : '',
              disableHoverListener: studyStatusData === null,
              children: /*#__PURE__*/_jsxs("div", {
                className: `relative underline cursor-pointer text-label-sm-500 text-my-blue-50 break-keep chat-header-button`,
                onClick: e => {
                  router.push(getManageStudentStudyContentUrlObject(studyStatusData.assignmentId ? StudyContentType.MY_ASSIGNMENT : studyStatusData.myStudyContentType === StudyContentType.MY_WRONG_TEST || studyStatusData.myStudyContentType === StudyContentType.MY_LEVEL_TEST ? StudyContentType.MY_TEST : studyStatusData.myStudyContentType, studyStatusData.assignmentId ? studyStatusData.assignmentId : studyStatusData.myStudyContentId, {
                    studentId: channelMetaData.studentId,
                    modalType: ProblemStatusModalType.STUDENT_ANSWER,
                    pageId: studyStatusData.pageId,
                    problemStatusId: studyStatusData.problemStatusId,
                    problemStatusDetailId: studyStatusData.problemStatusDetailId
                  }));
                  e.preventDefault();
                  e.stopPropagation();
                },
                children: [statusText, ": ", timeText]
              })
            }) : /*#__PURE__*/_jsx("div", {
              className: `text-label-sm-500 text-my-gray-60`,
              children: timeText
            }) : /*#__PURE__*/_jsx(_Fragment, {})]
          })
        })]
      }),
      renderRight: () => /*#__PURE__*/_jsx(Button, {
        variant: "Ghost",
        icon: /*#__PURE__*/_jsx(IconX, {}),
        size: "Medium",
        className: "chat-header-button",
        onClick: () => {
          onCloseGroupChannel();
          setIsChatPopupOpen(false);
        }
      })
    }), /*#__PURE__*/_jsx(PinnedMessageSection, {})]
  });
};
export default CustomChannelHeader;
const PinnedMessageSection = () => {
  const {
    data: user
  } = useUserInfoQuery();
  const isTeacher = user?.type === UserType.TEACHER;
  const {
    state: {
      currentChannel
    },
    actions: {
      scrollToMessage
    }
  } = useGroupChannel();
  if (!_.isNil(currentChannel?.lastPinnedMessage)) {
    return /*#__PURE__*/_jsxs(S.PinnedMessageSection, {
      className: "chat-announcement",
      onClick: () => scrollToMessage(currentChannel.lastPinnedMessage.createdAt, currentChannel.lastPinnedMessage.messageId),
      children: [/*#__PURE__*/_jsx("div", {
        className: "icon",
        children: /*#__PURE__*/_jsx(IconSpeakerphone, {
          size: 16,
          color: "var(--gray-30)"
        })
      }), /*#__PURE__*/_jsx("div", {
        className: "content",
        children: currentChannel.lastPinnedMessage.message
      }), isTeacher && /*#__PURE__*/_jsx("div", {
        className: "more-button",
        onClick: e => e.stopPropagation(),
        children: /*#__PURE__*/_jsx(DropdownActionMenu, {
          trigger: /*#__PURE__*/_jsx(Button, {
            variant: "Ghost",
            icon: /*#__PURE__*/_jsx(IconDotsVertical, {}),
            size: "Medium",
            onTouchEnd: e => e.stopPropagation()
          }),
          items: [{
            label: '공지 해제',
            onClick: async () => {
              await currentChannel.unpinMessage(currentChannel.lastPinnedMessage.messageId);
            }
          }],
          contentProps: {
            className: 'min-w-[7rem] z-[10000]'
          }
        })
      })]
    });
  }
};